import { Injectable } from '@angular/core';

import { Store } from '@ngxs/store';
import { TSTreeNode } from 'tsui';
import { UserState } from '@state/user/state';
import { AccountState } from '@state/account/state';
import { FeatureFlag, NewPermission, ReservedRole } from '@core/enums';

interface TSTreeNodeWithMetaData extends TSTreeNode {
  children?: TSTreeNodeWithMetaData[];
  permission?: NewPermission;
  featureFlag?: FeatureFlag | FeatureFlag[];
  internalOnly?: boolean;
}

@Injectable({ providedIn: 'root' })
export class SettingsService {
  private readonly _settingsOptions: TSTreeNodeWithMetaData[] = [
    {
      id: 18,
      name: 'Internal Admin Panel',
      icon: 'ts_lock-alt',
      iconColor: 'orange',
      link: 'settings/internal-admin-panel',
      expandable: false,
      internalOnly: true,
    },
    {
      id: 1,
      name: 'General',
      icon: 'ts_cog',
      link: 'settings/general',
      expandable: false,
      permission: NewPermission.SETTINGS_MANAGE_WORKSPACE,
    },
    {
      id: 2,
      name: 'User Management',
      icon: 'ts_users-alt',
      expandable: true,
      hideAddNew: true,
      children: [
        {
          id: 2.1,
          name: 'Users',
          icon: 'ts_users-alt',
          link: 'settings/users',
          expandable: false,
          permission: NewPermission.SETTINGS_MANAGE_USERS,
        },
        {
          id: 2.2,
          name: 'Teams',
          icon: 'ts_sitemap',
          link: 'settings/teams',
          expandable: false,
          permission: NewPermission.SETTINGS_MANAGE_TEAMS,
        },
        {
          id: 2.3,
          name: 'Permissions',
          icon: 'ts_shield',
          link: 'settings/roles',
          expandable: false,
          permission: NewPermission.SETTINGS_MANAGE_PERMISSIONS,
        },
        {
          id: 2.4,
          name: 'Roles',
          icon: 'ts_user-check',
          link: 'settings/task-roles',
          expandable: false,
          permission: NewPermission.SETTINGS_MANAGE_ROLES,
        },
        {
          id: 2.5,
          name: 'SAML/SSO',
          icon: 'ts_users-alt',
          link: 'settings/saml',
          expandable: false,
          permission: NewPermission.SETTINGS_MANAGE_WORKSPACE,
        },
      ],
    },
    {
      id: 3,
      name: 'Asset Management', // go to line 258
      icon: '',
      expandable: true,
      hideAddNew: true,
      children: [
        {
          id: 3.1,
          name: 'Chart of Accounts',
          icon: 'ts_list-ui-alt',
          link: 'settings/chart-of-accounts',
          expandable: false,
          featureFlag: FeatureFlag.ASSET_MANAGEMENT,
        },
        {
          id: 3.2,
          name: 'Accounts Mapping',
          icon: 'ts_sitemap',
          link: 'settings/accounts-mapping',
          expandable: false,
          featureFlag: FeatureFlag.ASSET_MANAGEMENT,
        },
      ],
    },
    {
      id: 4,
      name: 'Dropdowns',
      icon: 'ts_angle-double-down',
      link: 'settings/customizations',
      expandable: false,
      permission: NewPermission.SETTINGS_MANAGE_DROPDOWNS,
    },
    {
      id: 5,
      name: 'Fields',
      icon: 'ts_text',
      link: 'settings/custom_fields',
      expandable: false,
      permission: NewPermission.SETTINGS_MANAGE_FIELDS,
    },
    {
      id: 6,
      name: 'Templates',
      icon: 'ts_web-grid-alt',
      expandable: true,
      hideAddNew: true,
      children: [
        {
          id: 6.1,
          name: 'Budget',
          icon: 'ts_calculator',
          link: 'settings/templates/budget-builder',
          expandable: false,
          permission: NewPermission.SETTINGS_MANAGE_BUDGET_BUILDER_TEMPLATES,
          featureFlag: [FeatureFlag.ASSET_MANAGEMENT, FeatureFlag.BUDGET_BUILDER],
        },
        {
          id: 6.2,
          name: 'Folders',
          icon: 'ts_folder',
          link: 'settings/templates/folder-templates',
          expandable: false,
          permission: NewPermission.SETTINGS_MANAGE_FOLDER_RESOURCE_TEMPLATES,
        },
        {
          id: 6.3,
          name: 'Imports',
          icon: 'ts_import',
          link: 'settings/templates/import-templates',
          expandable: false,
          permission: NewPermission.SETTINGS_MANAGE_IMPORTS,
          featureFlag: FeatureFlag.GROUP_IMPORTS,
        },
        {
          id: 6.4,
          name: 'Tasks',
          icon: 'ts_task-gantt',
          link: 'settings/templates/task-lists',
          expandable: false,
          permission: NewPermission.SETTINGS_MANAGE_TASK_TEMPLATES,
        },
      ],
    },
    {
      id: 7,
      name: 'Sheets',
      icon: 'ts_table',
      link: 'settings/sheets',
      expandable: false,
      permission: NewPermission.SETTINGS_MANAGE_SHEETS,
    },
    {
      id: 8,
      name: 'Apps',
      icon: 'ts_apps',
      link: 'settings/integrations',
      expandable: false,
      permission: NewPermission.SETTINGS_MANAGE_APPS,
    },
    {
      id: 9,
      name: 'Code',
      icon: 'ts_brackets-curly',
      link: 'settings/functions',
      expandable: false,
      permission: NewPermission.SETTINGS_MANAGE_CODE,
    },
    {
      id: 10,
      name: 'Imports/Exports',
      icon: '',
      expandable: true,
      hideAddNew: true,
      children: [
        {
          id: 10.1,
          name: 'Imports',
          icon: 'ts_import',
          link: 'settings/imports',
          expandable: false,
          permission: NewPermission.SETTINGS_MANAGE_IMPORTS,
        },
        {
          id: 10.2,
          name: 'Exports',
          icon: 'ts_file-export',
          link: 'settings/jobs-status',
          expandable: false,
        },
      ],
    },
    {
      id: 11,
      name: 'Layouts',
      icon: 'ts_web-grid-alt',
      link: 'settings/layouts',
      expandable: false,
      permission: NewPermission.SETTINGS_MANAGE_LAYOUTS,
    },
    {
      id: 12,
      name: 'Automations',
      icon: 'ts_robot',
      link: 'settings/automations',
      expandable: false,
      permission: NewPermission.SETTINGS_MANAGE_AUTOMATIONS,
      featureFlag: FeatureFlag.AUTOMATIONS,
    },
    {
      id: 13,
      name: 'Objects Manager',
      icon: 'ts_cloud-database-tree',
      link: 'settings/objects_manager',
      permission: NewPermission.SETTINGS_MANAGE_OBJECTS_MANAGER,
    },
    {
      id: 14,
      name: 'Hooks',
      icon: 'ts_robot',
      link: 'settings/hooks',
      permission: NewPermission.SETTINGS_MANAGE_ACCOUNT_HOOKS,
      featureFlag: FeatureFlag.ACCOUNT_HOOKS,
    },
    {
      id: 15,
      name: 'API Keys',
      icon: 'vpn_key',
      link: 'settings/api-keys',
      expandable: false,
      permission: NewPermission.SETTINGS_MANAGE_WORKSPACE,
    },
    {
      id: 16,
      name: 'Audit',
      icon: 'ts_shield-check',
      link: 'settings/audit',
      permission: NewPermission.SETTINGS_MANAGE_AUDIT,
    },
    {
      id: 17,
      name: 'Webhooks',
      icon: 'ts_robot',
      link: 'settings/webhooks',
      permission: NewPermission.SETTINGS_MANAGE_ACCOUNT_HOOKS,
    },
  ];

  get settingsOptions(): TSTreeNode[] {
    const filterByPermission = ({ permission, id, internalOnly }: TSTreeNodeWithMetaData) => {
      if (internalOnly) {
        return this.store.selectSnapshot(UserState.isTermSheetUser);
      }
      if (id === 9 || id === 14) {
        const user = this.store.selectSnapshot(UserState.getUser);
        return user?.email?.includes('@termsheet.com') || user?.email?.includes('@intapp.com');
      }
      if (!permission) return true;

      return !!this.store.selectSnapshot(UserState.hasPermission(permission));
    };

    const filterByFeatureFlag = ({ featureFlag }: TSTreeNodeWithMetaData) => {
      // if a feature flag doesn't exist for the menu option, it can be shown
      if (!featureFlag) return true;

      // when several flags are provided, every one must be enabled
      const featureFlags = Array.isArray(featureFlag) ? featureFlag : [featureFlag];

      return featureFlags.every((flag) => this.store.selectSnapshot(AccountState.isFeatureEnabled(flag)));
    };

    const options = this._settingsOptions
      // filters top level options
      .filter(filterByPermission)
      .filter(filterByFeatureFlag)
      // filters children options
      .reduce((acc, treeNode) => {
        if (treeNode?.children && Array.isArray(treeNode?.children)) {
          const children = treeNode.children.filter(filterByPermission).filter(filterByFeatureFlag);

          return children.length > 0 ? [...acc, { ...treeNode, children }] : [...acc];
        }

        return [...acc, treeNode];
      }, []);

    // remove asset management options if user is not admin
    const user = this.store.selectSnapshot(UserState.getUser);
    if (user?.roles && !user.roles.some((role) => role.name === ReservedRole.Admin)) {
      return options.filter((option) => option.id !== 3);
    }

    return options;
  }

  constructor(private store: Store) {}
}
